/**
 * TwilioCostTrackerCard — Admin analytics for SMS / WhatsApp / Verify usage.
 *
 * Reads `/api/admin/twilio/cost-tracker` (super-admin only) and renders:
 *   · KPI tiles for last 7d, 30d, and lifetime cost
 *   · Per-channel breakdown
 *   · 14-day daily activity spark bars
 *   · Success rate + failure alert when > 5 %
 */
import React, { useEffect, useState } from "react";
import { MessageSquare, MessagesSquare, ShieldCheck, RefreshCw, DollarSign, IndianRupee, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Slice {
  count: number;
  by_kind: Record<string, number>;
  delivered: number;
  failed: number;
  success_rate: number | null;
  cost_usd: number;
  cost_inr: number;
}
interface DailyRow { date: string; count: number; cost_usd: number; }
interface Report {
  ok: boolean;
  rates_usd: Record<string, number>;
  inr_per_usd: number;
  last_7_days: Slice;
  last_30_days: Slice;
  lifetime: Slice;
  daily_last_14: DailyRow[];
  as_of: string;
}

const KIND_META: Record<string, { label: string; icon: any; color: string }> = {
  sms:           { label: "SMS",         icon: MessageSquare,  color: "text-sky-500" },
  whatsapp:      { label: "WhatsApp",    icon: MessagesSquare, color: "text-emerald-500" },
  verify_start:  { label: "Verify Send", icon: ShieldCheck,    color: "text-amber-500" },
  verify_check:  { label: "Verify Check",icon: ShieldCheck,    color: "text-neutral-400" },
};

export const TwilioCostTrackerCard: React.FC = () => {
  const [data, setData] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/twilio/cost-tracker");
      setData(r.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not load Twilio cost tracker");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const maxDaily = Math.max(1, ...(data?.daily_last_14 || []).map(d => d.count));

  return (
    <Card data-testid="admin-twilio-cost-card">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-500" />
            Twilio Cost Tracker
          </CardTitle>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            SMS + WhatsApp + Verify volume and estimated spend (rates from Twilio public pricing).
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="admin-twilio-cost-refresh"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && <div className="text-sm text-neutral-500">Loading…</div>}
        {data && (
          <>
            {/* KPI tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <KpiTile label="Last 7 days" slice={data.last_7_days} testid="kpi-7d" />
              <KpiTile label="Last 30 days" slice={data.last_30_days} testid="kpi-30d" />
              <KpiTile label="Lifetime" slice={data.lifetime} testid="kpi-life" />
            </div>

            {/* Failure alert */}
            {data.last_7_days.success_rate !== null && data.last_7_days.success_rate < 90 && data.last_7_days.count > 5 && (
              <div className="rounded-lg border border-amber-300 dark:border-amber-800/50 bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2 flex items-start gap-2"
                   data-testid="admin-twilio-cost-alert">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 dark:text-amber-200 leading-snug">
                  <span className="font-semibold">Delivery health: {data.last_7_days.success_rate}%</span> — {data.last_7_days.failed} of {data.last_7_days.count} messages failed in the last week. Consider checking Twilio console → Errors.
                </div>
              </div>
            )}

            {/* Per-channel breakdown */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-400 mb-2">
                Channel mix (last 30 days)
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(data.last_30_days.by_kind).map(([kind, count]) => {
                  const meta = KIND_META[kind] || { label: kind, icon: MessageSquare, color: "text-slate-400" };
                  const Icon = meta.icon;
                  const rate = data.rates_usd[kind] ?? 0;
                  const cost = rate * count;
                  return (
                    <div
                      key={kind}
                      data-testid={`admin-twilio-cost-kind-${kind}`}
                      className="rounded-lg border border-neutral-200 dark:border-white/10 p-2.5"
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                        <span className="text-xs font-medium">{meta.label}</span>
                      </div>
                      <div className="text-lg font-semibold tabular-nums">{count}</div>
                      <div className="text-[10px] text-neutral-500">
                        ≈ ${cost.toFixed(2)} / ₹{(cost * data.inr_per_usd).toFixed(0)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 14-day bar */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-400 mb-2">
                Daily volume (last 14 days)
              </div>
              <div className="flex items-end gap-1 h-20" data-testid="admin-twilio-cost-daily">
                {data.daily_last_14.map(d => {
                  const h = (d.count / maxDaily) * 100;
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                      <div className="w-full flex-1 flex items-end">
                        <div
                          className="w-full rounded-sm bg-gradient-to-t from-emerald-500 to-fuchsia-500 transition-all group-hover:opacity-80"
                          style={{ height: `${Math.max(2, h)}%` }}
                          title={`${d.date}: ${d.count} messages · $${d.cost_usd.toFixed(3)}`}
                        />
                      </div>
                      <div className="text-[8px] text-neutral-400 tabular-nums">{d.date.slice(-2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="text-[10px] text-neutral-400">
              Rate assumptions: SMS ${data.rates_usd.sms.toFixed(4)} / WhatsApp ${data.rates_usd.whatsapp.toFixed(4)} / Verify ${data.rates_usd.verify_start.toFixed(2)}.
              Costs approximate. INR converted at ₹{data.inr_per_usd.toFixed(0)}/USD. Updated {new Date(data.as_of).toLocaleString()}.
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const KpiTile: React.FC<{ label: string; slice: Slice; testid: string }> = ({ label, slice, testid }) => (
  <div className="rounded-xl border border-neutral-200 dark:border-white/10 p-3 bg-gradient-to-br from-emerald-50 to-fuchsia-50 dark:from-emerald-950/20 dark:to-fuchsia-950/20"
       data-testid={`admin-twilio-cost-${testid}`}>
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{label}</div>
    <div className="mt-1 text-2xl font-semibold tabular-nums">{slice.count}</div>
    <div className="text-xs text-neutral-500">messages sent</div>
    <div className="mt-2 flex items-center gap-3 text-sm">
      <span className="inline-flex items-center gap-1 font-semibold text-emerald-600 dark:text-emerald-400">
        <DollarSign className="h-3 w-3" /> {slice.cost_usd.toFixed(2)}
      </span>
      <span className="inline-flex items-center gap-1 text-neutral-500">
        <IndianRupee className="h-3 w-3" /> {slice.cost_inr.toFixed(0)}
      </span>
    </div>
    {slice.success_rate !== null && (
      <div className="mt-1 text-[10px] text-neutral-500">
        {slice.success_rate}% delivered · {slice.failed} failed
      </div>
    )}
  </div>
);

export default TwilioCostTrackerCard;
